<script setup lang="ts">
/**
 * Full-row category band for the grouped service types table. Selected by
 * `DynamicTable.rowOverride` for synthetic `kind: 'category-header'` rows
 * spliced in ahead of each category's types (same mechanism as
 * `DateHeaderRowCell`).
 *
 * One `<td colspan>`: the category name on the left (click opens the
 * category card) and the number of types on the right.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  row: {
    name: string
    count: number
    onOpen: () => void
  }
  colspan: number
}>()

const { t } = useI18n()

const countLabel = computed(() => t('units.serviceType', props.row.count))
</script>

<template>
  <td class="category-header-row font-tufte" :colspan="props.colspan">
    <div class="category-header-row__inner">
      <button type="button" class="category-header-row__name" @click.stop="props.row.onOpen()">
        {{ props.row.name }}
      </button>
      <span class="category-header-row__right">
        <span class="category-header-row__count">{{ countLabel }}</span>
      </span>
    </div>
  </td>
</template>

<style scoped>
.category-header-row {
  position: sticky;
  top: 0;
  z-index: 1;
  background: color-mix(in srgb, var(--color-tufte-bg) 85%, var(--color-money-positive) 15%);
  box-shadow: inset 0 -1px 0 var(--color-tufte-border);
  padding: 0.35rem 1rem !important;
  /* Same colspan trick as DateHeaderRowCell: don't let this cell widen the columns it spans. */
  width: 1px;
}

.category-header-row__inner {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  min-width: 0;
}

.category-header-row__name {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-sm);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--color-tufte-text) 75%, var(--color-money-positive));
}

.category-header-row__name:hover {
  color: var(--color-tufte-text);
}

.category-header-row__right {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
}

.category-header-row__count {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
}
</style>
